import { createFileRoute } from '@tanstack/react-router';
import { Construction, UserMinus, Users } from 'lucide-react';

import { EntityChip } from '@/components/primitives/EntityChip';
import { StateChip } from '@/components/primitives/StateChip';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { Skeleton } from '@/components/states/Skeleton';
import { StateBlock } from '@/components/states/StateBlock';
import {
  roleLabel,
  useLeagueMembers,
  type MembersResponse,
} from '@/features/leagues/league-detail';
import { useLeagueDetail } from '@/features/leagues/leagues';

export const Route = createFileRoute('/leagues/$leagueId/members')({
  component: MembersTab,
});

function MembersTab() {
  const { leagueId } = Route.useParams();
  const { data, isPending, isError, error, refetch } = useLeagueMembers(leagueId);
  const { data: league } = useLeagueDetail(leagueId);

  if (isError) {
    return <ErrorState error={error} subject="members" onRetry={() => void refetch()} />;
  }
  if (isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton height={120} />
        <Skeleton height={160} />
      </div>
    );
  }

  const vacant =
    league?.leagueSize != null ? Math.max(0, league.leagueSize - data.members.length) : null;

  return (
    <div className="flex flex-col gap-4">
      {/*
        Vacant teams get their own callout rather than being inferred from a count.
        A half-empty league is the most common reason a draft will not start, and it
        is invisible if the operator has to do the subtraction themselves.
      */}
      {vacant !== null && vacant > 0 && (
        <StateBlock
          icon={UserMinus}
          tone="warning"
          title={`${vacant} vacant ${vacant === 1 ? 'team' : 'teams'}`}
          description={`${data.members.length} of ${league?.leagueSize ?? '?'} spots are filled. A league below its size will not start a draft.`}
        />
      )}

      <Panel title="Current members" icon={Users} count={data.members.length}>
        {data.members.length === 0 ? (
          <EmptyState title="No members" description="Nobody has joined this league." />
        ) : (
          <ul className="flex flex-col gap-1.5">
            {data.members.map((member) => (
              <li
                key={member.userId}
                className="border-bd/60 flex flex-wrap items-center justify-between gap-2 border-b pb-1.5 last:border-b-0 last:pb-0"
              >
                <EntityChip
                  kind="user"
                  id={member.userId}
                  name={member.name}
                  secondary={member.squad?.name ?? undefined}
                  imageUrl={member.avatarUrl ?? undefined}
                  to={`/users/${member.userId}`}
                />
                <div className="flex flex-wrap items-center gap-1">
                  {member.roles.map((role) => (
                    <StateChip
                      key={role}
                      state={role}
                      size="sm"
                      label={roleLabel(role)}
                      tone={role === 'LeagueAdmin' ? 'warn' : 'neutral'}
                    />
                  ))}
                  <span className="mesh-mono text-tx3 text-[11px]">
                    {member.joinedAt ? member.joinedAt.slice(0, 10) : '—'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <FormerMembers data={data} />

      <Panel title="Squads" count={data.squads.length}>
        {data.squads.length === 0 ? (
          <EmptyState title="No squads" description="No teams exist in this league yet." />
        ) : (
          <ul className="flex flex-wrap gap-2">
            {data.squads.map((squad) => (
              <li
                key={squad.id}
                className="border-bd bg-elev flex items-center gap-2 rounded-lg border px-2.5 py-1.5"
              >
                <span className="text-tx text-[12.5px]">{squad.name ?? 'Unnamed'}</span>
                <span className="mesh-mono text-tx3 text-[11px]">{squad.memberCount}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <MembershipLog data={data} />
      <ActionsPanel />
    </div>
  );
}

/**
 * Former members, reconstructed from the event log.
 *
 * This panel is not a nicety. `leaguememberships` rows are HARD-DELETED on opt-out
 * and on commissioner removal, so without it the screen would assert that someone
 * was never in this league — and philanthropy obligations are computed from who was
 * a member on a given date.
 */
function FormerMembers({ data }: { data: MembersResponse }) {
  if (data.formerMembers.length === 0) return null;

  return (
    <Panel title="Former members" count={data.formerMembers.length}>
      <p className="text-tx3 mb-2 text-[11.5px]">
        Membership rows are deleted outright when someone leaves or is removed, so these people
        do not appear above. Rebuilt from the append-only event log — the same evidence the
        philanthropy ledger folds over.
      </p>
      <ul className="flex flex-col gap-1.5">
        {data.formerMembers.map((member) => (
          <li
            key={member.userId}
            className="border-bd/60 flex flex-wrap items-center justify-between gap-2 border-b pb-1.5 last:border-b-0 last:pb-0"
          >
            <EntityChip
              kind="user"
              id={member.userId}
              name={member.name}
              secondary={member.username ? `@${member.username}` : undefined}
              to={`/users/${member.userId}`}
            />
            <div className="flex flex-wrap items-center gap-1.5">
              <StateChip
                state={member.lastEvent}
                size="sm"
                tone={member.lastEvent === 'removed' ? 'bad' : 'muted'}
              />
              <span className="mesh-mono text-tx3 text-[11px]">
                {member.lastEventAt ? member.lastEventAt.slice(0, 10) : '—'}
              </span>
              {member.reason && (
                <span className="text-tx3 text-[11px] italic">{member.reason}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function MembershipLog({ data }: { data: MembersResponse }) {
  return (
    <Panel title="Membership event log" count={data.events.length}>
      {data.events.length === 0 ? (
        <EmptyState
          title="No recorded events"
          description="The event log has nothing for this league. On a league with members, that itself is worth investigating."
        />
      ) : (
        <ul className="flex flex-col gap-1">
          {data.events.map((event) => (
            <li key={event.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="mesh-mono text-tx3 text-[11px]">
                {event.at ? event.at.replace('T', ' ').slice(0, 16) : '—'}
              </span>
              <StateChip
                state={event.type}
                size="sm"
                tone={
                  event.type === 'removed' || event.type === 'reconciliation_repair'
                    ? 'bad'
                    : event.type === 'joined'
                      ? 'good'
                      : 'neutral'
                }
              />
              <span className="text-tx2 text-[12px]">{event.userName}</span>
              {event.reason && (
                <span className="text-tx3 text-[11px] italic">{event.reason}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * No controls. Remove member, transfer commissioner and force opt-in/opt-out are all
 * registered delegate operations, but ZazzBackend 403s any non-commissioner until
 * the `canManageAsAdmin` guard ships, so every one of them would fail. Saying that is
 * more useful than a row of buttons that do not work.
 */
function ActionsPanel() {
  return (
    <Panel title="Member actions">
      <StateBlock
        icon={Construction}
        tone="info"
        title="Blocked upstream, not unbuilt"
        description="Remove member, transfer commissioner, grant/revoke co-commissioner and force opt-in are all registered delegate operations. They are not offered here because ZazzBackend rejects any non-commissioner until the canManageAsAdmin guard is deployed — see OPEN_QUESTIONS item 2. They land the moment it does."
      />
    </Panel>
  );
}

function Panel({
  title,
  icon: Icon,
  count,
  children,
}: {
  title: string;
  icon?: typeof Users;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="border-bd bg-card rounded-xl border p-3.5">
      <h2
        className="font-head text-tx2 mb-2.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
        style={{ letterSpacing: '0.16em' }}
      >
        {Icon && <Icon size={12} aria-hidden />}
        {title}
        {count !== undefined && <span className="mesh-mono text-tx3 text-[11px]">{count}</span>}
      </h2>
      {children}
    </section>
  );
}
